import {
  AttachmentDetail_Download_Format,
  AttachmentDetail_SignatureStatus,
  AttachmentDetail_Status,
} from '@policyfly/protobuf/patrick'

import { bytesToObjectURL } from '@/utils/api'
import { ATTACHMENT_PENDING_KEY, ATTACHMENT_CONTENT_FIELD_KEY } from '@/utils/protobuf'

import type {
  AttachmentDetail,
  AttachmentDetail_ContentLocation,
  AttachmentDetail_ContentLocation_Field,
} from '@policyfly/protobuf/patrick'

/**
 * Whether an attachment is still being uploaded, and so cannot be acted on yet.
 */
export function isPendingAttachment (attachment: AttachmentDetail): boolean {
  return !!attachment[ATTACHMENT_PENDING_KEY]
}

/**
 * The statuses in which data has yet to arrive.
 * An unset status means the same as processing, so the two are treated alike.
 */
const PROCESSING_STATUSES = new Set<AttachmentDetail_Status>([
  AttachmentDetail_Status.STATUS_UNSPECIFIED,
  AttachmentDetail_Status.STATUS_PROCESSING,
])

/**
 * Whether an attachment or its preview is still being generated, and so is worth polling for.
 */
export function isAttachmentProcessing (attachment: AttachmentDetail): boolean {
  return PROCESSING_STATUSES.has(attachment.status) ||
    (!!attachment.preview && PROCESSING_STATUSES.has(attachment.preview.status))
}

/**
 * The statuses in which a signature has actually been provided,
 * whatever a reviewer has since made of it.
 */
const SIGNED_STATUSES = new Set<AttachmentDetail_SignatureStatus>([
  AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_SIGNED,
  AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_APPROVED,
  AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_REJECTED,
])

/**
 * Whether an attachment has been signed, regardless of whether or not the signature has been reviewed.
 */
export function isAttachmentSigned (attachment: AttachmentDetail): boolean {
  return SIGNED_STATUSES.has(attachment.signatureStatus)
}

/**
 * Whether an attachment has been signed but the signature has yet to be accepted or declined.
 */
export function isSignatureUnconfirmed (attachment: AttachmentDetail): boolean {
  return attachment.signatureStatus === AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_SIGNED
}

/**
 * The mime type of each format an attachment can be converted to.
 * The original is absent, as it keeps whatever mime type the attachment itself reports.
 */
const DOWNLOAD_FORMAT_MIME_TYPES: Record<AttachmentDetail_Download_Format, string> = {
  [AttachmentDetail_Download_Format.FORMAT_PDF]: 'application/pdf',
  [AttachmentDetail_Download_Format.FORMAT_XLSX]: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  [AttachmentDetail_Download_Format.FORMAT_DOCX]: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  [AttachmentDetail_Download_Format.FORMAT_ORIGINAL]: '',
  [AttachmentDetail_Download_Format.FORMAT_UNSPECIFIED]: '',
}

/**
 * Resolves a {@link AttachmentDetail_ContentLocation} to a url usable as an `href` or `src`.
 *
 * Bytes are materialised into an object url the first time they are used, and written back to the
 * location url for reuse. Revoke them with {@link revokeContentLocation}.
 *
 * @returns The url, or `null` while the content has not been loaded.
 */
export function contentLocationToUrl (
  location: AttachmentDetail_ContentLocation | undefined,
  mimeType?: string,
): string | null {
  if (!location) return null
  switch (location.location.oneofKind) {
    case 'url':
      return location.location.url || null
    case 'bytes': {
      const url = bytesToObjectURL(location.location.bytes, mimeType)
      location.location = { oneofKind: 'url', url }
      return url
    }
    default:
      return null
  }
}

/**
 * Revokes the object url a location holds if applicable and returns the location to being unloaded.
 *
 * A location that was loaded from a field points back at that field, so it can be loaded again.
 */
export function revokeContentLocation (location: AttachmentDetail_ContentLocation | undefined): void {
  const url = locationUrl(location)
  if (!url?.startsWith('blob:')) return
  URL.revokeObjectURL(url)
  const field = location![ATTACHMENT_CONTENT_FIELD_KEY]
  location!.location = field === undefined
    ? { oneofKind: undefined }
    : { oneofKind: 'field', field }
}

/**
 * The field of attachment data a location holds or points at, if known.
 */
export function contentLocationField (
  location: AttachmentDetail_ContentLocation | undefined,
): AttachmentDetail_ContentLocation_Field | undefined {
  if (location?.location.oneofKind === 'field') return location.location.field
  return location?.[ATTACHMENT_CONTENT_FIELD_KEY]
}

/**
 * Every content location on an attachment that holds or points at the field, loaded or not.
 */
export function attachmentFieldLocations (
  attachment: AttachmentDetail,
  field: AttachmentDetail_ContentLocation_Field,
): AttachmentDetail_ContentLocation[] {
  return [attachment.preview?.location, ...attachment.downloads.map((d) => d.location)]
    .filter((location): location is AttachmentDetail_ContentLocation => contentLocationField(location) === field)
}

/**
 * Fills a location with loaded content, recording the field it was loaded from
 * so that {@link revokeContentLocation} can point it back there.
 */
export function loadContentLocation (
  location: AttachmentDetail_ContentLocation,
  content: AttachmentDetail_ContentLocation,
): void {
  const field = contentLocationField(location)
  revokeContentLocation(location)
  if (field !== undefined) location[ATTACHMENT_CONTENT_FIELD_KEY] = field
  location.location = content.location
}

/**
 * Opens content in a new tab, either a url that is already available or one `content` resolves to.
 *
 * A url is opened immediately. Otherwise the tab is opened straight away and then pointed at the
 * resolved url, so call this synchronously from the click that asked for it, as a browser blocks a
 * tab opened after waiting on anything, such as content being streamed. The tab is closed again
 * when resolving fails or gives no url, and the error is rethrown.
 */
export async function openInNewTab (content: string | (() => Promise<string | null>)): Promise<void> {
  if (typeof content === 'string') {
    window.open(content, '_blank')
    return
  }
  const target = window.open('', '_blank')
  if (!target) throw new Error('Unable to open a new tab.')
  try {
    const url = await content()
    if (!url) throw new Error('There is no content to open.')
    target.location.href = url
  } catch (error) {
    target.close()
    throw error
  }
}

/**
 * Revokes every object url an attachment has materialised.
 * Call this when dropping an attachment, as its urls cannot be reached again afterwards.
 */
export function revokeAttachmentContent (attachment: AttachmentDetail): void {
  revokeContentLocation(attachment.preview?.location)
  for (const download of attachment.downloads) {
    revokeContentLocation(download.location)
  }
}

/**
 * Returns the url to render an attachment's preview from, if it has one that has been loaded.
 *
 * Will materialise any bytes into an object url and store it on the attachment,
 * see {@link contentLocationToUrl}. Revoke it with {@link revokeAttachmentContent}.
 */
export function attachmentPreviewUrl (attachment: AttachmentDetail): string | null {
  return contentLocationToUrl(attachment.preview?.location, DOWNLOAD_FORMAT_MIME_TYPES[AttachmentDetail_Download_Format.FORMAT_PDF])
}

/**
 * The url to download an attachment in the requested format, if it offers that format
 * and the content has been loaded.
 *
 * Will materialise any bytes into an object url and store it on the attachment,
 * see {@link contentLocationToUrl}. Revoke it with {@link revokeAttachmentContent}.
 */
export function attachmentDownloadUrl (
  attachment: AttachmentDetail,
  format: AttachmentDetail_Download_Format,
): string | null {
  return contentLocationToUrl(attachmentDownloadLocation(attachment, format), DOWNLOAD_FORMAT_MIME_TYPES[format] || attachment.mimeType)
}

/**
 * The location of the content an attachment offers for download in the requested format,
 * whether loaded or not, if it offers that format.
 */
export function attachmentDownloadLocation (
  attachment: AttachmentDetail,
  format: AttachmentDetail_Download_Format,
): AttachmentDetail_ContentLocation | undefined {
  return attachment.downloads.find((d) => d.format === format)?.location
}

/**
 * Applies loaded content from a cached attachment onto the incoming version of it.
 *
 * Content the incoming attachment has already loaded always wins, so a freshly read attachment
 * keeps its own data. Anything it has yet to load falls back to the cached content, so re-listing
 * does not discard what has already been fetched.
 *
 * Object urls left unused are revoked.
 */
export function preserveAttachmentContent (
  incoming: AttachmentDetail,
  cached: AttachmentDetail | undefined,
): AttachmentDetail {
  if (!cached) return incoming

  if (incoming.preview) preserveContentLocation(incoming.preview, cached.preview)
  // an attachment that no longer offers a preview will never reach the cached one again
  else revokeContentLocation(cached.preview?.location)

  for (const download of incoming.downloads) {
    preserveContentLocation(download, cached.downloads.find((d) => d.format === download.format))
  }
  for (const cachedDownload of cached.downloads) {
    const stillOffered = incoming.downloads.some((d) => d.format === cachedDownload.format)
    if (!stillOffered) revokeContentLocation(cachedDownload.location)
  }

  return incoming
}

/**
 * Calls {@link preserveAttachmentContent} on a list of attachments,
 * revoking the content of any cached attachment that is no longer listed.
 *
 * @param retained Attachments that are still held elsewhere. Lists share their attachment objects,
 * so one dropping an attachment does not mean its content can be released.
 */
export function preserveAttachmentListContent (
  incoming: AttachmentDetail[],
  existing: AttachmentDetail[],
  retained: AttachmentDetail[] = [],
): AttachmentDetail[] {
  const existingById = new Map(existing.map((attachment) => [attachment.id, attachment]))
  const keptIds = new Set([...incoming, ...retained].map((attachment) => attachment.id))

  for (const attachment of incoming) {
    preserveAttachmentContent(attachment, existingById.get(attachment.id))
  }
  for (const attachment of existing) {
    if (!keptIds.has(attachment.id)) revokeAttachmentContent(attachment)
  }

  return incoming
}

/**
 * Holds the content location of a preview or a download.
 */
interface ContentHolder {
  location?: AttachmentDetail_ContentLocation
}

/**
 * Keeps whichever of the two holders has loaded content, preferring the incoming one
 * and revoking an object url the cached one is left holding.
 */
function preserveContentLocation (incoming: ContentHolder, cached: ContentHolder | undefined): void {
  if (!cached) return
  if (!isContentLoaded(incoming.location)) {
    if (isContentLoaded(cached.location)) incoming.location = cached.location
    return
  }
  if (locationUrl(cached.location) !== locationUrl(incoming.location)) {
    revokeContentLocation(cached.location)
  }
}

/**
 * Whether a location points at content that is loaded and available to use.
 * This can be considered a check for whether the attachment is still in "lazy" state.
 */
export function isContentLoaded (location: AttachmentDetail_ContentLocation | undefined): boolean {
  switch (location?.location.oneofKind) {
    case 'url': return !!location.location.url
    case 'bytes': return !!location.location.bytes.length
    default: return false
  }
}

/**
 * Returns the url a location already holds without converting bytes like {@link contentLocationToUrl} does.
 */
function locationUrl (location: AttachmentDetail_ContentLocation | undefined): string | null {
  return location?.location.oneofKind === 'url' ? location.location.url : null
}
